import MenuItem from '@mui/material/MenuItem';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import useFindDifference from '../../../../hooks/useFindDifference';
import { useClientFields } from '../../../../sic-lib/api/hooks/useClientFields';
import { ViewProperty } from '../../../../sic-lib/types/ViewProperty';
import { hasMatchingFieldId } from '../../../../sic-lib/utils/functionalFilters';

const useStyles = makeStyles()({
    textField: {
        minWidth: '18ch',
    },
});

type Props = TextFieldProps & {
    viewProperties: ViewProperty[];
    onSelectHandler: React.Dispatch<{ viewProperties: ViewProperty[] }>;
};

export default function SelectViewProperties({ viewProperties, onSelectHandler, ...props }: Props) {
    const { classes } = useStyles();

    const { data } = useClientFields();

    const clientFields = React.useMemo(() => {
        if (!data) {
            return [];
        }
        return data;
    }, [data]);

    // filtering out some fields that are not available for view properties: -10 tags, -15 users, -20 segments
    const availableFields = useFindDifference(clientFields, viewProperties)
        .filter(field => field.fieldId !== -10 && field.fieldId !== -15 && field.fieldId !== -20)
        .sort((a, b) => a.label.localeCompare(b.label));

    const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const matchedField = clientFields.filter(hasMatchingFieldId(+event.target.value))[0];

        if (matchedField) {
            onSelectHandler({
                viewProperties: [
                    ...viewProperties,
                    {
                        fieldId: matchedField.fieldId,
                        orderNum: viewProperties.length,
                    },
                ],
            });
        }
    };

    return (
        <TextField
            {...(props as TextFieldProps)}
            select
            onChange={handleSelect}
            className={classes.textField}
        >
            <MenuItem value={-1} disabled>
                {props.label || 'Select View Property'}
            </MenuItem>
            {availableFields.map(fld => (
                <MenuItem key={fld.fieldId} value={fld.fieldId}>
                    {fld.label}
                </MenuItem>
            ))}
        </TextField>
    );
}
